import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ErrorToast from "./ErrorToast";
import { useSearchTags, Tag } from "../lib/api";
import { useTranslation } from "../hooks/useTranslation";

const SEARCH_DEBOUNCE_MS = 300;

function buildExplorerUrl(tagList: string[]): string {
  if (tagList.length === 0) return "/explorer";
  const query = tagList.map((t) => `tag=${encodeURIComponent(t)}`).join("&");
  return `/explorer?${query}`;
}

type PopularTagsProps = {
  /** Current tag filters from URL; user can remove one or add more */
  activeTags?: string[];
};

const PopularTags: React.FC<PopularTagsProps> = ({ activeTags: activeTagsProp }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeTags = Array.isArray(activeTagsProp)
    ? activeTagsProp.filter((t) => String(t).trim()).map((t) => String(t).trim().toLowerCase())
    : [];
  const [inputValue, setInputValue] = useState("");
  const [searchQ, setSearchQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQ(inputValue.trim().toLowerCase());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [inputValue]);

  const { data: tags = [], isLoading, error } = useSearchTags(searchQ, 20);

  const addTag = (tagName: string) => {
    const normalized = tagName.trim().toLowerCase();
    if (!normalized) return;
    if (activeTags.includes(normalized)) return;
    navigate(buildExplorerUrl([...activeTags, normalized]));
  };

  const removeTag = (tagName: string) => {
    const normalized = tagName.trim().toLowerCase();
    const next = activeTags.filter((t) => t !== normalized);
    navigate(buildExplorerUrl(next));
  };

  const handleTagClick = (tagName: string) => addTag(tagName);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const tag = inputValue.trim();
    if (tag) addTag(tag);
  };

  const shouldShowError = error && !error.message.includes("500") && !error.message.includes("سرور");
  const showResults = searchQ.length > 0;
  const hasResults = Array.isArray(tags) && tags.length > 0;
  const canGoToQuery = inputValue.trim().length > 0;
  const showSubmitButton = canGoToQuery && !hasResults;
  const hasActiveTags = activeTags.length > 0;

  return (
    <>
      {shouldShowError && (
        <ErrorToast
          message={error?.message || ""}
          isVisible={true}
          onClose={() => {}}
        />
      )}
      <div className="mx-0">
        <section
          className="bg-gray-50/80 dark:bg-gray-800/50 overflow-hidden"
          aria-label={t("popularTags.ariaSearch")}
        >
          <div className="p-3">
            {hasActiveTags && (
              <div className="mb-3 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-500 dark:text-gray-400">{t("popularTags.filter")}:</span>
                {activeTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="inline-flex items-center gap-1.5 min-h-[32px] px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/70 hover:bg-blue-200 dark:hover:bg-blue-800/80 text-blue-800 dark:text-blue-200 text-sm font-medium transition-colors cursor-pointer border border-blue-200/80 dark:border-blue-700/80 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-1 dark:focus:ring-offset-gray-800"
                    aria-label={t("popularTags.removeFilter", { tag })}
                  >
                    #{tag}
                    <span className="text-blue-600 dark:text-blue-300 font-bold leading-none" aria-hidden>×</span>
                  </button>
                ))}
                {activeTags.length > 1 && (
                  <button
                    type="button"
                    onClick={() => navigate("/explorer")}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 underline"
                  >
                    {t("popularTags.clearAll")}
                  </button>
                )}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="relative">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={t("popularTags.searchPlaceholder")}
                  className="w-full min-h-[44px] rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 py-2 text-sm focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-400/50 focus:border-blue-500 dark:focus:border-blue-400 outline-none transition-all duration-200 border-transparent focus:bg-white dark:focus:bg-gray-800"
                  aria-label={t("popularTags.ariaSearch")}
                  autoComplete="off"
                />
              </div>
            </form>

            {showResults && (
              <div
                className="mt-4 pt-3 border-t border-gray-200/80 dark:border-gray-700/80 min-h-[2.5rem]"
                style={{ animation: "fadeInScale 0.2s ease-out" }}
                role="status"
                aria-live="polite"
                aria-busy={isLoading}
              >
                {isLoading ? (
                  <div className="flex flex-wrap gap-2" aria-hidden>
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="h-8 rounded-full bg-gray-200 dark:bg-gray-600 animate-pulse"
                        style={{ width: 56 + i * 24 }}
                      />
                    ))}
                  </div>
                ) : hasResults ? (
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag: Tag, index: number) => (
                      <button
                        key={tag.id ?? index}
                        type="button"
                        onClick={() => handleTagClick(tag.name)}
                        className="min-h-[36px] px-4 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/70 hover:bg-blue-200 dark:hover:bg-blue-800/80 active:scale-95 text-blue-800 dark:text-blue-200 text-sm font-medium transition-all duration-200 cursor-pointer border border-blue-200/80 dark:border-blue-700/80 hover:border-blue-300 dark:hover:border-blue-600 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-1 dark:focus:ring-offset-gray-800"
                      >
                        #{tag.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-1">
                    {t("popularTags.noTagsFound")}
                  </p>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default PopularTags;
